import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, ChevronRight, User } from 'lucide-react';

interface LoginScreenProps {
  onUnlock: () => void;
}

export function LoginScreen({ onUnlock }: LoginScreenProps) {
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const handleUnlock = () => {
    setIsUnlocking(true);
    setTimeout(() => {
      onUnlock();
    }, 800);
  };

  // Current time display
  const now = new Date();
  const timeString = now.toLocaleTimeString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit',
    hour12: true 
  });
  const dateString = now.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-99998 flex flex-col items-center justify-center overflow-hidden"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0, scale: 1.1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Blurred background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1475274047050-1d0c0975c63e?w=1920')",
            filter: 'blur(20px) brightness(0.4)',
            transform: 'scale(1.1)',
          }}
        />

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/50" />

        {/* Content */}
        <motion.div
          className="relative z-10 flex flex-col items-center"
          animate={isUnlocking ? { y: -100, opacity: 0, scale: 0.9 } : {}}
          transition={{ duration: 0.5 }}
        >
          {/* Time */}
          <motion.div
            className="text-center mb-12"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-8xl font-light text-white mb-2 tracking-tight">
              {timeString}
            </h1>
            <p className="text-xl text-white/80">
              {dateString}
            </p>
          </motion.div>

          {/* Glass Card Container */}
          <motion.div
            className="relative p-8 rounded-3xl flex flex-col items-center"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `,
            }}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {/* Specular highlight */}
            <div 
              className="absolute inset-0 rounded-3xl pointer-events-none"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%)',
              }}
            />

            {/* Avatar */}
            <motion.div
              className="relative mb-6"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="w-28 h-28 rounded-full overflow-hidden border-4 border-white/20 shadow-2xl">
                {/* Avatar image or placeholder */}
                <div className="w-full h-full bg-blue-600 flex items-center justify-center">
                  <User className="w-14 h-14 text-white/90" />
                </div>
              </div>
              
              {/* Online indicator */}
              <motion.div
                className="absolute bottom-1 right-1 w-5 h-5 bg-green-500 rounded-full border-3 border-white/50"
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              />
            </motion.div>

            {/* Name */}
            <motion.h2
              className="text-2xl font-medium text-white mb-1"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Lukman Afandi
            </motion.h2>

            <motion.p
              className="text-white/60 text-sm mb-6"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Full Stack Developer
            </motion.p>

            {/* Unlock button - Glass style */}
            <motion.button
              className="relative group flex items-center gap-3 px-8 py-3 rounded-full text-white transition-all duration-300 overflow-hidden"
              style={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              }}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              onClick={handleUnlock}
              onMouseEnter={() => setShowHint(true)}
              onMouseLeave={() => setShowHint(false)}
              whileHover={{ 
                scale: 1.05,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Button highlight */}
              <span 
                className="absolute inset-0 rounded-full pointer-events-none"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 50%)',
                }}
              />
              <Lock className="w-5 h-5 relative z-10" />
              <span className="font-medium relative z-10">Click to Enter</span>
              <motion.div
                className="relative z-10"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <ChevronRight className="w-5 h-5" />
              </motion.div>
            </motion.button>

            {/* Hint */}
            <AnimatePresence>
              {showHint && (
                <motion.p
                  className="mt-4 text-white/50 text-sm"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  Welcome to my portfolio!
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

        {/* Bottom hint */}
        <motion.div
          className="absolute bottom-8 flex flex-col items-center gap-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <motion.div
            className="w-1 h-8 rounded-full bg-white/30"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          <p className="text-white/40 text-xs">Scroll or click to continue</p>
        </motion.div>

        {/* Keyboard shortcuts hint */}
        <motion.div
          className="absolute bottom-8 right-8 text-white/30 text-xs"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          Press any key or click to unlock
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
